import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { trackEvent } from '../../analytics/sml/analytics';
import { TypedMemo } from '../../common/types';
import { Music24Logo } from '../components/Music24Logo';
import { Music24FullLogo } from '../components/Music24FullLogo';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { assetShareMessages } from './assetShareMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the share page; each part uses its own slice of it */
import styles from './AssetShare.module.scss';
/* eslint-enable css-modules/no-unused-class */

type ShareKind = 'kitAsset' | 'reel';

/** "Make yours free": the sign-up page of the web app. */
const MakeYours: React.FC<{ kind: ShareKind; className?: string }> = ({ kind, className }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const onClick = useCallback(() => {
    trackEvent('public_share_cta_tapped', { kind, cta: 'startFree' });
    router.push(SMLRoutes.auth);
  }, [kind, router]);
  return (
    <button type="button" className={className} onClick={onClick} data-testid="asset-share-make-yours">
      {intl.formatMessage(assetShareMessages.makeYours)}
    </button>
  );
};

/**
 * The growth line on a phone (design asset-share c3, c4): the Music24 mark, "Made with Music24",
 * one line of benefit and "Make yours free".
 */
const AssetShareHookComponent: React.FC<{ kind: ShareKind; className?: string }> = ({ kind, className }) => {
  const intl = useIntl();
  return (
    <div className={clsx(styles.hook, className)} data-testid="asset-share-hook">
      <Music24Logo size={22} className={styles.hookMark} />
      <p className={styles.hookText}>
        <span className={styles.hookTitle}>{withBrandWord(intl.formatMessage(assetShareMessages.madeWith))}</span>
        {intl.formatMessage(assetShareMessages.hookText)}
      </p>
      <MakeYours kind={kind} className={styles.hookGo} />
    </div>
  );
};

export const AssetShareHook = TypedMemo(AssetShareHookComponent);

/**
 * The short growth line (design asset-share c1, c5, c6): the Music24 logo and "Make yours free".
 * The word of the logo uses `currentColor`, so it reads on light, on dark and on a video.
 */
const AssetShareFootComponent: React.FC<{ kind: ShareKind; onMedia?: boolean; className?: string }> = ({
  kind,
  onMedia = false,
  className,
}) => {
  const intl = useIntl();
  return (
    <div className={clsx(styles.foot, onMedia && styles.footOnMedia, className)} data-testid="asset-share-foot">
      <Music24FullLogo size={18} className={styles.footLogo} label={intl.formatMessage(assetShareMessages.madeWith)} />
      <span aria-hidden>·</span>
      <MakeYours kind={kind} className={styles.footGo} />
    </div>
  );
};

export const AssetShareFoot = TypedMemo(AssetShareFootComponent);
